// Pose a GLB face skeleton from ORL joint deltas (9/joint: t cm, r Maya-xyz deg, s).
// Notes: ~/comments/aos-threejs-poc/faceJointDrive_applyJointDeltas_compose.md

import * as THREE from 'three'

const DEGREES_TO_RADIANS = Math.PI / 180
const scratchEuler = new THREE.Euler()
const scratchQuaternion = new THREE.Quaternion()

/** Bind `jointNames` to bones under `root`, capturing neutral locals. Null when none match. */
export function captureFaceJointRig(root, jointNames) {
  const boneByName = new Map()
  root.traverse((node) => { if (node.isBone) boneByName.set(node.name, node) })
  const bones = jointNames.map((name) => boneByName.get(name) ?? null)
  const matched = bones.filter(Boolean).length
  if (!matched) return null
  const neutral = bones.map((bone) => bone && {
    position: bone.position.clone(),
    quaternion: bone.quaternion.clone(),
    scale: bone.scale.clone(),
  })
  return { bones, neutral, matched }
}

/** Apply one evaluateRig jointOut frame: local = T(tn+td) · R(rn)·R(rd) · S(sn·(1+sd)). */
export function applyJointDeltas(rig, jointOut) {
  const { bones, neutral } = rig
  for (let joint = 0; joint < bones.length; joint++) {
    const bone = bones[joint]
    if (!bone) continue
    const rest = neutral[joint]
    const at = joint * 9
    bone.position.set(
      rest.position.x + jointOut[at],
      rest.position.y + jointOut[at + 1],
      rest.position.z + jointOut[at + 2])
    // Maya extrinsic xyz (Rz·Ry·Rx) is three's intrinsic 'ZYX' — the pack manifest says so.
    scratchEuler.set(
      jointOut[at + 3] * DEGREES_TO_RADIANS,
      jointOut[at + 4] * DEGREES_TO_RADIANS,
      jointOut[at + 5] * DEGREES_TO_RADIANS, 'ZYX')
    bone.quaternion.copy(rest.quaternion).multiply(scratchQuaternion.setFromEuler(scratchEuler))
    bone.scale.set(
      rest.scale.x * (1 + jointOut[at + 6]),
      rest.scale.y * (1 + jointOut[at + 7]),
      rest.scale.z * (1 + jointOut[at + 8]))
  }
}
